// The one-time move of an earlier build's queued lot creates and edits into lot states (BL-2681,
// ADR 070 — Ingest Protocol § 7). It runs when a scope's store opens, before the queue accepts
// writes, and is safe to run on every open: it finds nothing once the entries are gone.
import { isLegacyStateOp, legacyEntriesToStates, type LegacyEntry } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state-migrate';
import type { OfflineStore } from '../../gateways/native-device';

/**
 * Fold the queue's lot creates and edits into each lot's desired state, and remove those entries, in
 * one batch: a crash leaves the entries or the states, never half of each.
 *
 * @param store - the scope's open store.
 * @returns how many entries were moved.
 * @throws when the store refuses the batch.
 * @sideEffect Writes lot states and deletes queue entries.
 */
export async function migrateLegacyQueue(store: OfflineStore): Promise<number> {
  const entries = await store.outbox.list();
  // Most opens have nothing to move: read the stored states only when there is.
  if (!entries.some((entry) => isLegacyStateOp(entry.op))) return 0;
  const { states, migratedIds } = legacyEntriesToStates({ entries: entries as LegacyEntry[], tenantId: store.scope.tenantId, existing: await store.lotStates.list() });
  if (migratedIds.length === 0) return 0;
  const result = await store.outbox.appendLocalWrite({
    entries: [],
    lots: { put: [], delete: [] },
    lotStates: { put: states, delete: [] },
    removeEntryIds: migratedIds,
  });
  if (!result.success) throw new Error('change queue: earlier lot changes could not be moved to the new queue');
  return migratedIds.length;
}
